/**
 * SocketContext — real-time price and notification delivery via Socket.io.
 *
 * Exposes:
 *   prices          — { [SYMBOL]: { price, change_pct } }
 *   simDate         — string | null
 *   connected       — boolean
 *   onOrderFilled    — register a callback for order_filled events
 *   onOrderCancelled — register a callback for order_cancelled events
 *   onPositionExited — register a callback for position_exited events
 *   offOrderFilled   — unregister callback
 *   offOrderCancelled — unregister callback
 *   offPositionExited — unregister callback
 *
 * On reconnect: emits a 'reconnected' signal so pages can re-fetch missed state.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';

const SocketContext = createContext({
  prices:               {},
  simDate:              null,
  connected:            false,
  subscribePrices:      () => {},
  onOrderFilled:        () => {},
  onOrderCancelled:     () => {},
  onPositionExited:     () => {},
  offOrderFilled:       () => {},
  offOrderCancelled:    () => {},
  offPositionExited:    () => {},
  onBalanceUpdated:     () => {},
  offBalanceUpdated:    () => {},
  onBannerUpdated:      () => {},
  offBannerUpdated:     () => {},
});

// In production, prefer the configured backend URL. In local dev, bypass the
// CRA dev-server proxy for Socket.IO and talk to the exposed backend port.
const getSocketUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) return process.env.REACT_APP_BACKEND_URL;
  if (window.location.port === '3000') {
    return `${window.location.protocol}//${window.location.hostname}:8000`;
  }
  return window.location.origin;
};

const SOCKET_URL = getSocketUrl();

export function SocketProvider({ children }) {
  const { token } = useAuth();
  const socketRef  = useRef(null);

  const [prices,    setPrices]    = useState({});
  const [simDate,   setSimDate]   = useState(null);
  const [connected, setConnected] = useState(false);

  // Tracks the last F&O room requested so we can re-subscribe on reconnect
  const currentFoRoomRef = useRef(null);

  // Subscriber registries for per-event callbacks
  const orderFilledCallbacks     = useRef(new Set());
  const orderCancelledCallbacks  = useRef(new Set());
  const positionExitedCallbacks  = useRef(new Set());
  const balanceUpdatedCallbacks  = useRef(new Set());
  const bannerUpdatedCallbacks   = useRef(new Set());
  const reconnectCallbacks       = useRef(new Set());

  const onOrderFilled      = useCallback((cb) => { orderFilledCallbacks.current.add(cb);     return () => orderFilledCallbacks.current.delete(cb);     }, []);
  const offOrderFilled     = useCallback((cb) => { orderFilledCallbacks.current.delete(cb);  }, []);
  const onOrderCancelled   = useCallback((cb) => { orderCancelledCallbacks.current.add(cb);  return () => orderCancelledCallbacks.current.delete(cb);  }, []);
  const offOrderCancelled  = useCallback((cb) => { orderCancelledCallbacks.current.delete(cb); }, []);
  const onPositionExited   = useCallback((cb) => { positionExitedCallbacks.current.add(cb);  return () => positionExitedCallbacks.current.delete(cb);  }, []);
  const offPositionExited  = useCallback((cb) => { positionExitedCallbacks.current.delete(cb); }, []);
  const onBalanceUpdated  = useCallback((cb) => { balanceUpdatedCallbacks.current.add(cb); return () => balanceUpdatedCallbacks.current.delete(cb); }, []);
  const offBalanceUpdated = useCallback((cb) => { balanceUpdatedCallbacks.current.delete(cb); }, []);
  const onBannerUpdated   = useCallback((cb) => { bannerUpdatedCallbacks.current.add(cb);  return () => bannerUpdatedCallbacks.current.delete(cb);  }, []);
  const offBannerUpdated  = useCallback((cb) => { bannerUpdatedCallbacks.current.delete(cb); }, []);
  const onReconnect       = useCallback((cb) => { reconnectCallbacks.current.add(cb);       return () => reconnectCallbacks.current.delete(cb);       }, []);

  // Subscribe to a price room on the server.
  // 'equities' (default) — all non-F&O symbols; always received, call to leave an F&O room.
  // 'fo:NIFTY50' / 'fo:BANKNIFTY' — F&O contract prices for that underlying.
  const subscribePrices = useCallback((room) => {
    currentFoRoomRef.current = room;
    const socket = socketRef.current;
    if (socket && socket.connected) {
      socket.emit('subscribe_prices', { room });
    }
    // If not yet connected, the connect handler will re-emit on first connect.
  }, []);

  useEffect(() => {
    if (!token) return;

    const socket = io(SOCKET_URL, {
      path:                 '/socket.io/socket.io',
      auth:                 { token },
      transports:           ['websocket', 'polling'],
      reconnection:         true,
      reconnectionDelay:    2000,
      reconnectionAttempts: Infinity,
    });

    socketRef.current = socket;
    let isFirstConnect = true;

    socket.on('connect', () => {
      setConnected(true);
      // Re-subscribe to any F&O room that was requested (covers first connect
      // when the page called subscribePrices before the socket was ready, and reconnects)
      if (currentFoRoomRef.current) {
        socket.emit('subscribe_prices', { room: currentFoRoomRef.current });
      }
      if (!isFirstConnect) {
        // Reconnect after a drop — notify pages to re-fetch missed state
        reconnectCallbacks.current.forEach(cb => cb());
      }
      isFirstConnect = false;
    });

    socket.on('disconnect', () => setConnected(false));

    socket.on('prices', (data) => {
      if (data?.prices) setPrices(prev => ({ ...prev, ...data.prices }));
      if (data?.sim_date) setSimDate(data.sim_date);
    });

    socket.on('order_filled', (data) => {
      const dir = data.order_type === 'BUY' ? 'Bought' : 'Sold';
      toast.success(`${dir} ${data.quantity} × ${data.symbol} @ ₹${Number(data.fill_price).toFixed(2)}`, {
        duration: 5000,
        icon: data.order_type === 'BUY' ? '📈' : '📉',
      });
      orderFilledCallbacks.current.forEach(cb => cb(data));
    });

    socket.on('position_exited', (data) => {
      const pnl  = data.pnl ?? 0;
      const sign = pnl >= 0 ? '+' : '';
      const msg  = `${data.symbol} exited (${data.exit_reason}) — ${sign}₹${Math.abs(pnl).toFixed(0)}`;
      if (pnl >= 0) toast.success(msg, { duration: 6000, icon: '✅' });
      else          toast.error(msg,    { duration: 6000, icon: '🔴' });
      positionExitedCallbacks.current.forEach(cb => cb(data));
    });

    socket.on('order_cancelled', (data) => {
      toast(`${data.symbol} order cancelled (EOD)`, { duration: 4000, icon: '🚫' });
      orderCancelledCallbacks.current.forEach(cb => cb(data));
    });

    socket.on('order_failed', (data) => {
      toast.error(`Order failed: ${data.symbol} — ${data.error_code || 'unknown error'}`, {
        duration: 5000,
      });
    });

    socket.on('balance_updated', (data) => {
      balanceUpdatedCallbacks.current.forEach(cb => cb(data));
    });

    socket.on('banner_updated', (data) => {
      bannerUpdatedCallbacks.current.forEach(cb => cb(data));
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token]);

  return (
    <SocketContext.Provider value={{
      prices, simDate, connected,
      subscribePrices,
      onOrderFilled, offOrderFilled,
      onOrderCancelled, offOrderCancelled,
      onPositionExited, offPositionExited,
      onBalanceUpdated, offBalanceUpdated,
      onBannerUpdated, offBannerUpdated,
      onReconnect,
    }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  return useContext(SocketContext);
}
